import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Filter } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const STATUS = [
  { value: "available",    label: "Available",      tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { value: "coming_soon",  label: "Coming Soon",    tone: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "seasonal",     label: "Seasonal",       tone: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "limited",      label: "Limited",        tone: "bg-rose-100 text-rose-800 border-rose-300" },
  { value: "out_of_stock", label: "Out of Stock",   tone: "bg-zinc-200 text-zinc-700 border-zinc-300" },
];
const sm = (s) => STATUS.find((x) => x.value === s) || STATUS[0];

const BLANK = {
  name: "", color_hex: "#FFC93C", category: "water",
  description: "", ingredients: [], image_url: "",
  status: "available", is_active: true, sort_order: 0,
};

export default function AdminFlavors() {
  const [worlds, setWorlds] = useState([]);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    const url = filter === "all" ? "/admin/flavors" : `/admin/flavors?category=${filter}`;
    api.get(url).then((r) => setItems(r.data || []));
  }, [filter]);

  useEffect(() => { api.get("/admin/worlds").then((r) => setWorlds(r.data || [])); }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing({ ...BLANK, category: filter !== "all" ? filter : (worlds[0]?.slug || "water") }); setOpen(true); };
  const startEdit = (f) => { setEditing({ ...f }); setOpen(true); };

  const save = async () => {
    const body = { ...editing };
    delete body.id; delete body.created_at; delete body.updated_at;
    try {
      if (editing.id) await api.put(`/admin/flavors/${editing.id}`, body);
      else await api.post("/admin/flavors", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this flavour?")) return;
    await api.delete(`/admin/flavors/${id}`); toast.success("Deleted"); load();
  };
  const toggleActive = async (f) => {
    const body = { ...f, is_active: !f.is_active };
    delete body.id; delete body.created_at;
    await api.put(`/admin/flavors/${f.id}`, body);
    load();
  };

  return (
    <div data-testid="admin-flavors" className="space-y-8">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Flavours by World</div>
          <h1 className="editorial-h1 text-5xl mt-2">{items.length} flavours.</h1>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55"><Filter size={12} /> World</div>
          <select className="pp-input bg-transparent" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="af-filter" style={{ width: "auto" }}>
            <option value="all">All worlds</option>
            {worlds.map((w) => <option key={w.slug} value={w.slug}>{w.label}</option>)}
          </select>
          <button onClick={startNew} data-testid="af-new" className="btn-pill btn-dark text-sm"><Plus size={16} /> New flavour</button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        <button data-testid="af-tab-all" onClick={() => setFilter("all")} className={`btn-pill text-xs ${filter === "all" ? "btn-dark" : "btn-ghost border border-brand-line"}`}>All</button>
        {worlds.map((w) => (
          <button key={w.slug} data-testid={`af-tab-${w.slug}`} onClick={() => setFilter(w.slug)} className={`btn-pill text-xs ${filter === w.slug ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{w.label}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="af-grid">
        {items.map((f) => {
          const s = sm(f.status);
          return (
            <div key={f.id} className="card-elevated overflow-hidden" data-testid={`af-card-${f.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-brand-muted relative">
                {f.image_url
                  ? <img src={f.image_url} alt={f.name} className="h-full w-full object-cover" />
                  : <div className="h-full w-full grid place-items-center text-4xl" style={{ background: (f.color_hex || "#FFC93C") + "22" }}>🍦</div>}
                <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] border rounded-full px-2.5 py-1 ${s.tone}`}>{s.label}</span>
                {!f.is_active && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] border border-brand-charcoal/30 rounded-full px-2.5 py-1 bg-white">Hidden</span>}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-xl leading-tight">{f.name}</h3>
                  <span className="inline-block w-3 h-3 rounded-full shrink-0 mt-2" style={{ background: f.color_hex }} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-charcoal/55 mt-1">{f.category}</div>
                {f.description && <p className="mt-2 text-xs text-brand-charcoal/70 line-clamp-2">{f.description}</p>}
              </div>
              <div className="p-3 flex items-center justify-between gap-2 border-t border-brand-line">
                <button onClick={() => toggleActive(f)} className="btn-pill btn-ghost text-xs border border-brand-line">{f.is_active ? "Hide" : "Show"}</button>
                <div className="flex gap-2">
                  <button data-testid={`af-edit-${f.id}`} onClick={() => startEdit(f)} className="btn-pill btn-ghost text-xs border border-brand-line">Edit</button>
                  <button onClick={() => del(f.id)} className="btn-pill btn-ghost text-xs text-brand-red"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="text-brand-charcoal/60 col-span-full py-12 text-center">No flavours in this world yet. Add one.</div>}
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-3xl">{editing.id ? "Edit" : "New"} flavour</div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Name</label><input data-testid="af-f-name" className="pp-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">World</label>
                <select data-testid="af-f-world" className="pp-input bg-transparent" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {worlds.map((w) => <option key={w.slug} value={w.slug}>{w.label}</option>)}
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Status</label>
                <select data-testid="af-f-status" className="pp-input bg-transparent" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Brand Colour</label><input type="color" className="h-10 w-20 border border-brand-line rounded-md mt-1 block" value={editing.color_hex || "#FFC93C"} onChange={(e) => setEditing({ ...editing, color_hex: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Sort order</label><input type="number" className="pp-input" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Image URL</label><input data-testid="af-f-image" className="pp-input" value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://..." /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Description</label><textarea data-testid="af-f-desc" rows="3" className="pp-input" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Ingredients (comma-separated)</label>
                <input data-testid="af-f-ings" className="pp-input" value={(editing.ingredients || []).join(", ")} onChange={(e) => setEditing({ ...editing, ingredients: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Mango, Milk, Cane sugar" /></div>
              <div className="sm:col-span-2"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Visible on website</label></div>
            </div>
            <div className="mt-8 flex justify-end gap-3"><button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button><button onClick={save} data-testid="af-f-save" className="btn-pill btn-dark text-sm">Save flavour</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
